/*
 * Purpose: Canonical average-user outcome seeds — factory regression SSOT
 * Last modified: 2026-06-25 | Agent
 * Completeness: 92
 */
export interface AverageOutcomeSeed {
  id: string;
  label: string;
  outcomeText: string;
  expectedTemplateId: string;
  /** Minimum AutoBlocks connector IDs the factory must resolve */
  expectedConnectorIds: string[];
  dryRunFixture: Record<string, unknown>;
  keywords: string[];
}

export const AVERAGE_OUTCOME_SEEDS: AverageOutcomeSeed[] = [
  {
    id: 'linkedin_job_hunt',
    label: 'LinkedIn job hunt pipeline',
    outcomeText:
      'When LinkedIn emails me about new jobs, score fit, tailor my resume, apply, track it, and remind me to follow up.',
    expectedTemplateId: 'linkedin_job_hunt_pipeline',
    expectedConnectorIds: ['ab.gmail', 'ab.openai', 'ab.logic', 'ab.http', 'ab.storage', 'ab.notify'],
    keywords: ['linkedin', 'job', 'resume', 'apply'],
    dryRunFixture: {
      from: 'jobs-noreply@linkedin.com',
      subject: 'New jobs: Senior Engineer',
      roles: [
        { company: 'Acme Corp', title: 'Senior Engineer', url: 'https://linkedin.com/jobs/123', score: 85 },
      ],
    },
  },
  {
    id: 'job_application_tracker',
    label: 'Job application tracker',
    outcomeText: 'Log every job application I make with company, role, and date.',
    expectedTemplateId: 'job_application_tracker',
    expectedConnectorIds: ['ab.storage', 'ab.notify', 'ab.logic'],
    keywords: ['track application', 'log application', 'job tracker'],
    dryRunFixture: {
      company: 'Acme Corp',
      role: 'Senior Engineer',
      channel: 'linkedin',
      status: 'applied',
    },
  },
  {
    id: 'job_followup_reminder',
    label: 'Job follow-up reminder',
    outcomeText: 'Remind me to follow up on job applications when I have not heard back after 7 days.',
    expectedTemplateId: 'job_followup_reminder',
    expectedConnectorIds: ['ab.storage', 'ab.scheduler', 'ab.notify'],
    keywords: ['follow up', 'follow-up', 'no response'],
    dryRunFixture: {
      company: 'Acme Corp',
      role: 'Senior Engineer',
      appliedAt: new Date(Date.now() - 8 * 86400000).toISOString(),
      status: 'applied',
      followUpDue: true,
    },
  },
  {
    id: 'receipt_expense',
    label: 'Receipt to expense tracker',
    outcomeText: 'When I get a receipt email, add the amount to my expense tracker.',
    expectedTemplateId: 'receipt_email_to_tracker',
    expectedConnectorIds: ['ab.gmail', 'ab.logic', 'ab.storage'],
    keywords: ['receipt', 'expense', 'invoice'],
    dryRunFixture: {
      from: 'receipts@store.com',
      subject: 'Your purchase receipt',
      merchant: 'Coffee Shop',
      total: 4.5,
      currency: 'USD',
    },
  },
  {
    id: 'appointment_reminder',
    label: 'Appointment reminder (24h)',
    outcomeText: 'Remind me 24 hours before any appointment I book.',
    expectedTemplateId: 'appointment_reminder',
    expectedConnectorIds: ['ab.gmail', 'ab.scheduler', 'ab.notify'],
    keywords: ['appointment', 'booking', '24 hour', 'remind'],
    dryRunFixture: {
      title: 'Dental checkup',
      location: 'Main Street Clinic',
      startTime: new Date(Date.now() + 86400000).toISOString(),
    },
  },
];

export function matchOutcomeSeed(outcomeText: string): AverageOutcomeSeed | undefined {
  const lower = outcomeText.toLowerCase().trim();
  const exact = AVERAGE_OUTCOME_SEEDS.find((s) => s.outcomeText.toLowerCase() === lower);
  if (exact) return exact;

  let best: { seed: AverageOutcomeSeed; score: number } | undefined;
  for (const seed of AVERAGE_OUTCOME_SEEDS) {
    let score = 0;
    for (const kw of seed.keywords) {
      if (lower.includes(kw)) score += kw.split(' ').length > 1 ? 3 : 1;
    }
    if (!best || score > best.score) best = { seed, score };
  }
  return best && best.score > 0 ? best.seed : undefined;
}

export function getSeedById(id: string): AverageOutcomeSeed | undefined {
  return AVERAGE_OUTCOME_SEEDS.find((s) => s.id === id);
}
